import * as THREE from 'three';
import * as TSL from 'three/tsl';
import { MeshStandardNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';
import { ISerializableObjectTag } from '../../../utils/serialization/serializable.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** World-space triplanar stripe pattern on `MeshStandardNodeMaterial` for terrain-like shading. */
@EngineClass('WorldTriplanarNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'World-Space Triplanar',
  nodeMaterialGroup: 'TSL Node FX',
})
export class WorldTriplanarNodeMaterialAsset extends NodeMaterialAsset(MeshStandardNodeMaterial) {
  readonly [ISerializableObjectTag] = true as const;

  @property({ type: 'color', description: 'Dark stripe color' })
    colorDark = new THREE.Color(0.05, 0.05, 0.05);

  @property({ type: 'color', description: 'Light stripe color' })
    colorLight = new THREE.Color(1.0, 1.0, 1.0);

  @property({ type: 'number', min: 0.1, max: 20.0, step: 0.1, description: 'Stripe scale in world units' })
    scale = 2.0;

  @property({ type: 'number', min: 0.0, max: 1.0, step: 0.01, description: 'Roughness' })
  override roughness = 0.6;

  constructor() {
    super();
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    this.roughnessNode = TSL.float(this.roughness);
    this.metalnessNode = TSL.float(0.0);

    const blend = TSL.abs(TSL.normalWorld);
    const bSum = blend.x.add(blend.y).add(blend.z).add(TSL.float(0.0001));
    const bx = blend.x.div(bSum);
    const by = blend.y.div(bSum);
    const bz = blend.z.div(bSum);
    const scaleNode = TSL.float(this.scale);
    const stripeX = TSL.sin(TSL.positionWorld.y.mul(scaleNode)).mul(TSL.float(0.5)).add(TSL.float(0.5));
    const stripeY = TSL.sin(TSL.positionWorld.x.mul(scaleNode)).mul(TSL.float(0.5)).add(TSL.float(0.5));
    const stripeZ = TSL.cos(TSL.positionWorld.x.mul(scaleNode).add(TSL.positionWorld.y.mul(scaleNode))).mul(TSL.float(0.5)).add(TSL.float(0.5));
    const mixed = stripeX.mul(bx).add(stripeY.mul(by)).add(stripeZ.mul(bz));
    const colorDarkNode = TSL.color(this.colorDark);
    const colorLightNode = TSL.color(this.colorLight);
    this.colorNode = TSL.mix(colorDarkNode, colorLightNode, mixed);
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): WorldTriplanarNodeMaterialAsset {
    const instance = new WorldTriplanarNodeMaterialAsset();
    WorldTriplanarNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
